import { create } from "zustand";

import kutil from "@modules/kutil";
import commonCode from "@modules/common_code";

export interface MFCStatus {
  id: number;
  ver: number; // 버전

  isStarted: boolean; // 기동여부
};

export const defMFCStatus: MFCStatus = {
  id: 0,
  ver: -1,

  isStarted: false
};

export interface WorkDetail {
  workDetailNo: number; // 작업상세번호
  workNo: number; // 작업번호

  startPos: string; // 출발위치
  endPos: string; // 목적위치
  workOrder: number; // 작업순서
  workType: string; // 작업종류

  workEquip: string; // 작업설비
  startPosEquip: string; // 출발위치(설비)
  endPosEquip: string; // 목적위치(설비)
  floor: string; // 층
  currPosEquip: string; // 현재위치(설비)
  workStatusEquip: string; // 작업상태(설비)
  errorNo: string; // 에러번호
  delayCause: string; // 대기원인(코드)
  workStartDate: string; // 작업시작일시
  workEndDate: string; // 작업종료일시
  workNoMfc: number; // 작업번호(MFC)
  workNoPlc: number; // 작업번호(PLC)

  delayCauseDesc: string; // 대기원인
  memo: string; // 참고사항
};

export const defWorkDetail: WorkDetail = {
  workDetailNo: 0,
  workNo: 0,

  startPos: "",
  endPos: "",
  workOrder: 0,
  workType: "",

  workEquip: "",
  startPosEquip: "",
  endPosEquip: "",
  floor: "",
  currPosEquip: "",
  workStatusEquip: "",
  errorNo: "",
  delayCause: "",
  workStartDate: "",
  workEndDate: "",
  workNoMfc: 0,
  workNoPlc: 0,

  delayCauseDesc: "",
  memo: "",
};

function calcWorkDetail(workDetail: WorkDetail) {
  workDetail.delayCauseDesc = "[" + workDetail.delayCause + "] " + commonCode.getCodeValue("delayCause", workDetail.delayCause);
  if (workDetail.errorNo?.startsWith("M")) {
    workDetail.memo = commonCode.getCodeValue("mfcErrorNo", workDetail.errorNo);
  }
}

export interface SCStatus {
  id: number;
  ver: number; // 버전

  isComm: boolean; // 통신여부
  isSccAuto: boolean; // 기상반(SCC) 자동여부
  isSctAuto: boolean; // 지상반(SCT) 자동여부

  workNoMfc: number; // 작업번호
  workNoPlc: number; // 작업번호
  forkType: string; // 포크상태 포크중심(C), 포크좌측(L), 포크우측(R)
  workType: string; // 작업유형 입고작업(I), 출고작업(O)
  isLoad: boolean; // 화물유무
  posX: number; // 주행현재위치
  posY: number; // 승강현재위치
  isError: boolean; // 에러여부
  errorCode1: number; // 에러코드1
  errorCode2: number; // 에러코드2

  mode: string; // manual-manual(mm), manual-auto(ma), auto-manual(am), auto-auto(aa)
  status: string; // disconnected, normal, error
};

export const defSCStatus: SCStatus = {
  id: 0,
  ver: -1,

  isComm: false,
  isSccAuto: false,
  isSctAuto: false,

  workNoMfc: 0,
  workNoPlc: 0,
  forkType: "C",
  workType: "",
  isLoad: false,
  posX: 0,
  posY: 0,
  isError: false,
  errorCode1: 0,
  errorCode2: 0,

  mode: "mm",
  status: "disconnected",
};

function calcSCMode(status: SCStatus): string {
  if (status.isComm) {
    if (status.isSccAuto) {
      return status.isSctAuto ? "aa" : "am";
    } else {
      return status.isSctAuto ? "ma" : "mm";
    }
  } else {
    return "mm";
  }
}

function calcSCStatus(status: SCStatus): string {
  if (status.isComm) {
    if (status.isError) {
      return "error";
    } else {
      return "normal";
    }
  } else {
    return "disconnected";
  }
}

function fixSCStatus(status: SCStatus): void {
  status.mode = calcSCMode(status);
  status.status = calcSCStatus(status);
}

export interface RVStatus {
  id: number;
  ver: number; // 버전

  isComm: boolean; // 통신여부
  isSccAuto: boolean; // 기상반(SCC) 자동여부
  isSctAuto: boolean; // 지상반(SCT) 자동여부

  workNoMfc: number; // 작업번호
  workNoPlc: number; // 작업번호
  workType: string; // 작업유형 입고작업(I), 출고작업(O)
  isLoad: boolean; // 적재여부
  pos: number; // 주행현재위치
  isError: boolean; // 에러여부
  errorCode1: number; // 에러코드1
  errorCode2: number; // 에러코드2

  mode: string; // manual-manual(mm), manual-auto(ma), auto-manual(am), auto-auto(aa)
  status: string; // disconnected, normal, error
};

export const defRVStatus: RVStatus = {
  id: 0,
  ver: -1,

  isComm: false,
  isSccAuto: false,
  isSctAuto: false,

  workNoMfc: 0,
  workNoPlc: 0,
  workType: "",
  isLoad: false,
  pos: 1,
  isError: false,
  errorCode1: 0,
  errorCode2: 0,

  mode: "mm",
  status: "disconnected",
};

function calcRVMode(status: RVStatus): string {
  if (status.isComm) {
    if (status.isSccAuto) {
      return status.isSctAuto ? "aa" : "am";
    } else {
      return status.isSctAuto ? "ma" : "mm";
    }
  } else {
    return "mm";
  }
}

function calcRVStatus(status: RVStatus): string {
  if (status.isComm) {
    if (status.isError) {
      return "error";
    } else {
      return "normal";
    }
  } else {
    return "disconnected";
  }
}

function fixRVStatus(status: RVStatus): void {
  status.mode = calcRVMode(status);
  status.status = calcRVStatus(status);
}

export interface CVStatus {
  id: number;
  ver: number; // 버전
  type: string; // NORMAL, IN, OUT

  isComm: boolean; // 통신여부
  isAuto: boolean; // 자동여부
  isEmer: boolean; // 비상정지여부

  workNoMfc: number; // 작업번호(외부)
  workNoPlc: number; // 작업번호
  isActive: boolean; // 동작여부
  isError: boolean; // 에러여부
  isLoad: boolean; // 적재여부
  isLifterAvailable: boolean; // LIFTER 이용가능 여부
  isMovingAvailable: boolean; // 이동가능 여부

  // LIFT, BLOCK 처리
  blockType: string; // 차단방향 "", LEFT, RIGHT, UP, DOWN
  isBlock: boolean; // 차단여부

  labelPrefix: string; // label prefix
  labelPostfix: string; // label postfix

  mode: string; // auto, manual, auto-emergency, manual-emergency, disconnected
  status: string; // active, idle, error
  text: string; //
};

export const defCVStatus: CVStatus = {
  id: 0,
  ver: -1,
  type: "NORMAL",

  isComm: false,
  isAuto: false,
  isEmer: false,

  workNoMfc: 0,
  workNoPlc: 0,
  isActive: false,
  isError: true,
  isLoad: false,
  isLifterAvailable: false,
  isMovingAvailable: false,

  blockType: "",
  isBlock: false,

  labelPrefix: "",
  labelPostfix: "",

  mode: "disconnected",
  status: "error",
  text: "",
};

function calcCVMode(status: CVStatus): string {
  if (status.isComm) {
    if (status.isAuto && status.isEmer == false) {
      return "auto";
    } else if (status.isAuto == false && status.isEmer == false) {
      return "manual";
    } else if (status.isAuto && status.isEmer) {
      return "auto-emergency";
    } else if (status.isAuto == false && status.isEmer) {
      return "manual-emergency";
    }
  }
  return "disconnected";
}

function calcCVStatus(status: CVStatus): string {
  if (status.isError) {
    return "error";
  } else if (status.isActive) {
    return "active";
  } else {
    return "idle";
  }
}

function calcCVText(status: CVStatus): string {
  let text = "";
  if (status.labelPrefix) {
    text += status.labelPrefix + " ";
  }
  if (status.workNoMfc > 0) {
    text += status.workNoMfc;
  } else {
    text += status.id;
  }
  if (status.labelPostfix) {
    text += " " + status.labelPostfix;
  }
  return text.replaceAll("<br />", "\n")
}

function fixCVStatus(status: CVStatus): void {
  status.mode = calcCVMode(status);
  status.status = calcCVStatus(status);
  status.text = calcCVText(status);
}

const range = (start: number, end: number): number[] => Array.from({ length: end - start + 1 }, (_, i) => i + start);

interface PlcStoreState {
  mfcStatus: MFCStatus;
  workDetails: Map<number, WorkDetail>;
  scStatuses: Map<number, SCStatus>;
  rvStatuses: Map<number, RVStatus>;
  cvStatuses: Map<number, CVStatus>;
  init: () => void;
  updateMFCStatus: (partial: Partial<MFCStatus>) => void;
  updateWorkDetails: (partials: Partial<WorkDetail>[]) => void;
  updateWorkDetail: (partial: Partial<WorkDetail>) => void;
  updateSCStatus: (partial: Partial<SCStatus>) => void;
  updateRVStatus: (partial: Partial<RVStatus>) => void;
  updateCVStatus: (partial: Partial<CVStatus>) => void;
}

const CV_BLOCK_TYPES: Record<number, string> = {
  109: "UP",
  110: "RIGHT",
  112: "UP",

  148: "UP",
  149: "RIGHT",
  151: "UP",

  185: "UP",
  186: "RIGHT",
  188: "UP",

  205: "DOWN",
  212: "UP",
  219: "DOWN",
  226: "UP",

  306: "UP",
  403: "UP",
};

const CV_TYPES: Record<number, string> = {
  101: "OUT", 122: "IN",
  123: "OUT", 139: "IN",
  140: "OUT", 160: "IN",
  161: "OUT", 176: "IN",
  177: "OUT", 197: "IN",

  201: "OUT", 207: "IN",
  208: "OUT", 214: "IN",
  215: "OUT", 221: "IN",
  222: "OUT", 228: "IN",

  301: "OUT", 309: "IN",
  310: "OUT", 317: "IN",
  318: "OUT", 325: "IN",
  326: "OUT", 333: "IN",
  334: "OUT", 341: "IN",
};

function createInitialState() {
  const mfcStatus: MFCStatus = kutil.deepCopy(defMFCStatus);

  const scStatuses = new Map<number, SCStatus>();
  range(1, 7).forEach(idx => {
    const status = kutil.deepCopy(defSCStatus);
    status.id = idx;
    fixSCStatus(status);
    scStatuses.set(status.id, status);
  });

  // rvStatuses, cvStatuses도 기존 init() 로직과 동일하게
  const rvStatuses = new Map<number, RVStatus>();
  range(1, 1).forEach(idx => {
    const status = kutil.deepCopy(defRVStatus);
    status.id = idx;
    fixRVStatus(status);
    rvStatuses.set(status.id, status);
  });

  const cvStatuses = new Map<number, CVStatus>();
  range(101, 403).forEach(idx => {
    const status = kutil.deepCopy(defCVStatus);
    status.id = idx;
    status.blockType = CV_BLOCK_TYPES[idx] ?? status.blockType;
    status.type = CV_TYPES[idx] ?? status.type;
    fixCVStatus(status);
    cvStatuses.set(status.id, status);
  });

  return {
    mfcStatus,
    workDetails: new Map<number, WorkDetail>(),
    scStatuses,
    rvStatuses,
    cvStatuses,
  };
}

export const usePlcStore = create<PlcStoreState>((set, get) => ({
  ...createInitialState(),
  init: () => set(createInitialState()),
  updateMFCStatus: (partial: Partial<MFCStatus>): void => {
    const currStatus: MFCStatus = get().mfcStatus;
    const newStatus: MFCStatus = Object.assign(kutil.deepCopy(currStatus), partial);
    set({ mfcStatus: newStatus }); 
  },
  updateWorkDetails: (partials: Partial<WorkDetail>[]): void => {
    const newWorkDetails = new Map<number, WorkDetail>();
    partials.forEach(partial => {
      const newWorkDetail: WorkDetail = Object.assign(kutil.deepCopy(defWorkDetail), partial);
      calcWorkDetail(newWorkDetail);
      newWorkDetails.set(newWorkDetail.workNo, newWorkDetail);
    });
    set({ workDetails: newWorkDetails }); 
    console.log(newWorkDetails);
  },
  updateWorkDetail: (partial: Partial<WorkDetail>): void => {
    const currWorkDetail: WorkDetail | undefined = get().workDetails.get(partial.workNo!);
    const newWorkDetail: WorkDetail = Object.assign(kutil.deepCopy(currWorkDetail ?? defWorkDetail), partial);
    calcWorkDetail(newWorkDetail);

    const newWorkDetails = new Map(get().workDetails);
    newWorkDetails.set(newWorkDetail.workNo, newWorkDetail);
    set({ workDetails: newWorkDetails }); 
  },
  updateSCStatus: (partial: Partial<SCStatus>): void => {
    const currStatus: SCStatus = get().scStatuses.get(partial.id!)!;
    const newStatus: SCStatus = Object.assign(kutil.deepCopy(currStatus), partial);
    if (currStatus.ver != newStatus.ver) {
      fixSCStatus(newStatus);

      const newStatuses = new Map(get().scStatuses);
      newStatuses.set(newStatus.id, newStatus);
      set({ scStatuses: newStatuses }); 
    }
  },
  updateRVStatus: (partial: Partial<RVStatus>): void => {
    const currStatus: RVStatus = get().rvStatuses.get(partial.id!)!;
    const newStatus: RVStatus = Object.assign(kutil.deepCopy(currStatus), partial);
    if (currStatus.ver != newStatus.ver) {
      fixRVStatus(newStatus);

      const newStatuses = new Map(get().rvStatuses);
      newStatuses.set(newStatus.id, newStatus);
      set({ rvStatuses: newStatuses }); 
    }
  },
  updateCVStatus: (partial: Partial<CVStatus>): void => {
    const currStatus: CVStatus = get().cvStatuses.get(partial.id!)!;
    const newStatus: CVStatus = Object.assign(kutil.deepCopy(currStatus), partial);
    if (currStatus.ver != newStatus.ver) {
      fixCVStatus(newStatus);

      const newStatuses = new Map(get().cvStatuses);
      newStatuses.set(newStatus.id, newStatus);
      set({ cvStatuses: newStatuses }); 
    }
  },
}));
